// "Go to slide 33", from the box at the top of the outline drawer.
//
// The number an author types is the one the deck already shows them — the badge
// on a slide's thumbnail and the left half of the toolbar's "64 / 66" — which
// is the slide's place in the running deck, counting from 1. Everything the
// app holds internally counts from 0, so the conversion lives here rather than
// being written out at each of the places that needs it.

/** How many digits a slide number could plausibly need. */
const MAX_DIGITS = 4;

/**
 * What may sit in the box: digits, and not too many of them.
 *
 * Applied as the author types, so the field can never hold something that is
 * not a slide number in the first place — no minus signs, no exponent "e", none
 * of the things a native number input quietly accepts.
 */
export const sanitizeSlideJumpInput = (raw) =>
  String(raw ?? '').replace(/\D/g, '').slice(0, MAX_DIGITS);

/**
 * Read the box against the deck it is pointing into.
 *
 * @param {string} raw what the author has typed
 * @param {number} slideCount how many slides the deck has
 * @returns {{number: number|null, index: number|null, isValid: boolean, isEmpty: boolean, isOutOfRange: boolean}}
 *   `number` is the 1-based slide number, `index` its 0-based position.
 *   `isOutOfRange` is deliberately false while the box is empty: an author who
 *   has typed nothing yet has not made a mistake.
 */
export const resolveSlideJump = (raw, slideCount) => {
  const cleaned = sanitizeSlideJumpInput(raw);
  const total = Number.isInteger(slideCount) && slideCount > 0 ? slideCount : 0;
  const isEmpty = cleaned === '';
  const parsed = isEmpty ? null : Number.parseInt(cleaned, 10);
  const isValid = Number.isInteger(parsed) && parsed >= 1 && parsed <= total;

  return {
    number: isValid ? parsed : null,
    index: isValid ? parsed - 1 : null,
    isValid,
    isEmpty,
    isOutOfRange: !isEmpty && !isValid
  };
};
